import { getBackendUrl } from "@Utils";
import { t } from "@i18n";
import axiosWrapper from "@AxiosWrapper";
import { setBrowserPopupBlocked } from "../reducers/browserPopupSlice";
import { store } from "@Store";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "./constants";

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

function prepareDownloadData(data: any) {
  let objCount = 1;
  let fileVersionIds, folderIds, issuesIds, formId, projectId, mailId, downloadAllThreads;
  if (data.fileVersionIds || data.folderIds) {
    objCount = data.fileVersionIds.length + data.folderIds.length;
    fileVersionIds = data.fileVersionIds.join(",");
    folderIds = data.folderIds.join(",");
  } else if (data.issuesIds) {
    objCount = data.issuesIds.length;
    issuesIds = data.issuesIds.join(",");
  } else if (data.formData) {
    formId = data.formData.formId;
    projectId = data.formData.projectId;
  } else if (data.mailId) {
    mailId = data.mailId;
    projectId = data.projectId;
    downloadAllThreads = data.downloadAllThreads || false;
  }
  return { objCount, fileVersionIds, folderIds, issuesIds, formId, projectId, mailId, downloadAllThreads };
}

function setFileAndFolderParams(baseUrl: any, data: any, fileVersionIds: any, folderIds: any) {
  baseUrl.searchParams.set("fileVersionIds", fileVersionIds);
  baseUrl.searchParams.set("folderIds", folderIds);
  if (data.transmittalNo) {
    baseUrl.searchParams.set("fileName", data.fileName);
    baseUrl.searchParams.set("transmittalNo", data.transmittalNo);
  }
}

function setFormParams(baseUrl: any, data: any, formId: any, projectId: any) {
  baseUrl.searchParams.set("formId", formId);
  baseUrl.searchParams.set("projectId", projectId);
  if (data.formData.paperSize) {
    baseUrl.searchParams.set("paperSize", data.formData.paperSize);
  }
  if (data.formData.tableSectionFormat) {
    baseUrl.searchParams.set("tableSectionFormat", data.formData.tableSectionFormat);
  }
}

function setEstimateParams(baseUrl: any, data: any) {
  baseUrl.searchParams.set("estimateId", data.estimateId);
  if (data.versionId) {
    baseUrl.searchParams.set("versionId", data.versionId);
  }
}

function setMailParams(baseUrl: any, data: any, projectId: any) {
  baseUrl.searchParams.set("mailId", data.mailId);
  baseUrl.searchParams.set("projectId", projectId);
  if (data.downloadAllThreads) {
    baseUrl.searchParams.set("downloadAllThreads", data.downloadAllThreads);
  }
}

function setUrlParameters(baseUrl: any, data: any, preparedData: any) {
  const { fileVersionIds, folderIds, issuesIds, formId, projectId } = preparedData;

  if (fileVersionIds || folderIds) {
    setFileAndFolderParams(baseUrl, data, fileVersionIds, folderIds);
  } else if (issuesIds) {
    baseUrl.searchParams.set("issuesIds", issuesIds);
  } else if (formId) {
    setFormParams(baseUrl, data, formId, projectId);
  } else if (data.estimateId) {
    setEstimateParams(baseUrl, data);
  } else if (data.reviewId) {
    baseUrl.searchParams.set("reviewId", data.reviewId);
  } else if (data.mailId) {
    setMailParams(baseUrl, data, projectId);
  }
  baseUrl.searchParams.set("origin", location.hostname);
}

function handleCallbackDownload(data: any, preparedData: any, callback: any) {
  const callbackUrl = new URL(`${getBackendUrl()}/fileSystemServer/${data.type}`);
  const { fileName } = data;

  callbackUrl.searchParams.set("fileVersionIds", preparedData.fileVersionIds);
  callbackUrl.searchParams.set("fileName", fileName);

  axiosWrapper.get(callbackUrl.toString(), { responseType: "blob" }).then((response: any) => {
    const finalFileName = data.fileName || data.name;
    callback(response.data, finalFileName);
  });
}

function handleTinyUrlDownload(data: any, preparedData: any) {
  const { fileVersionIds, folderIds, issuesIds } = preparedData;

  axiosWrapper
    .post(`${getBackendUrl()}/fileSystemServer/createTinyUrl`, {
      fileVersionIds,
      folderIds,
      issuesIds,
    })
    .then((res: any) => {
      const tinyUrl = new URL(`${getBackendUrl()}/fileSystemServer/${data.type}`);
      tinyUrl.searchParams.set("tinyUrl", res.data.tinyUrl);
      tinyUrl.searchParams.set("origin", location.hostname);

      const newWindow = redirectDownload(tinyUrl.toString(), data);
      store.dispatch(setBrowserPopupBlocked(!newWindow));
    });
}

function handleRegularDownload(data: any, preparedData: any) {
  const baseUrl = new URL(`${getBackendUrl()}/fileSystemServer/${data.type}`);
  setUrlParameters(baseUrl, data, preparedData);

  const newWindow = redirectDownload(baseUrl.toString(), data);
  store.dispatch(setBrowserPopupBlocked(!newWindow));
}

export const downloadFile = (data: any, callback?: any) => {
  const preparedData = prepareDownloadData(data);

  if (callback) {
    handleCallbackDownload(data, preparedData, callback);
    return;
  }

  if (preparedData.objCount > 80) {
    handleTinyUrlDownload(data, preparedData);
  } else {
    handleRegularDownload(data, preparedData);
  }
};

function redirectDownload(url: any, data: any) {
  if (data.mobileFileManager) {
    const nameCondition = data.fileVersionIds.length == 1 && data.folderIds.length == 0;
    (globalThis as unknown as Window).ReactNativeWebView?.postMessage(
      JSON.stringify({
        action: "fileDownload",
        data: {
          url,
          fileName: nameCondition
            ? data.fileName
            : `CUBE_Download_${new Date()
                .toLocaleDateString("en-IN", {
                  year: "2-digit",
                  month: "2-digit",
                  day: "2-digit",
                })
                .replaceAll("/", "-")}.zip`,
        },
      }),
    );
    return true;
  }
  const popup = globalThis.open(url, "_blank");
  return !!popup;
}

export const enum EXPORT_TYPE {
  PDF = "PDF",
  EXCEL = "EXCEL",
}
interface PdfExportParams {
  issuesIds?: string[];
  formId?: string | null;
  projectId?: string | null;
  paperSize?: string | null;
  tableSectionFormat?: string | null;
  estimateId?: string | null;
  versionId?: string | null;
  reviewId?: string | null;
  mailId?: string | null;
  downloadAllThreads?: boolean;
  exportType?: EXPORT_TYPE;
}

export async function exportPdf(params: PdfExportParams) {
  try {
    // Build query string
    const queryParams = new URLSearchParams();

    if (params.issuesIds) {
      queryParams.append("issuesIds", params.issuesIds.join(","));
    }
    if (params.formId) {
      queryParams.append("formId", params.formId);
    }
    if (params.projectId) {
      queryParams.append("projectId", params.projectId);
    }
    if (params.paperSize) {
      queryParams.append("paperSize", params.paperSize);
    }
    if (params.tableSectionFormat) {
      queryParams.append("tableSectionFormat", params.tableSectionFormat);
    }
    if (params.estimateId) {
      queryParams.append("estimateId", params.estimateId);
    }
    if (params.versionId) {
      queryParams.append("versionId", params.versionId);
    }
    if (params.reviewId) {
      queryParams.append("reviewId", params.reviewId);
    }
    if (params.mailId) {
      queryParams.append("mailId", params.mailId);
    }
    if (params.downloadAllThreads !== undefined) {
      queryParams.append("downloadAllThreads", String(params.downloadAllThreads));
    }

    // Create export job
    const response = await axiosWrapper.get(`${getBackendUrl()}/fileSystemServer/pdf-export?${queryParams.toString()}`);

    if (!response.data.status) {
      throw new Error(response.data.message || "Failed to create export job");
    }

    // Show success toast
    store.dispatch(
      showToastNotification({
        content: t("PDF export started. You'll be notified when ready."),
        type: TOAST_TYPE_SUCCESS,
      }),
    );

    return {
      status: true,
      jobId: response.data.jobId,
    };
  } catch (error: any) {
    console.error("❌ PDF export error:", error);
    store.dispatch(
      showToastNotification({
        content: error.message || "Failed to create PDF export",
        type: TOAST_TYPE_ERROR,
      }),
    );

    return {
      status: false,
      error: error.message,
    };
  }
}
